import { generateCodeChallenge, generateCodeVerifier } from './pkce';
import { MICROSOFT_AUTHORIZE_ENDPOINT, MICROSOFT_GRAPH_SCOPE, MICROSOFT_POPUP_TIMEOUT_MS, type MicrosoftAuthState, type MicrosoftPopupResult, getClientConfig } from 'vince-core/constants/microsoft';
import { type Authentication, ExternalProvider, type ExternalProviderAdapter } from 'vince-core/types/auth';

const authenticateWithPopup = async (): Promise<Authentication> => {
    const config = getClientConfig();
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    const nonce = crypto.randomUUID();
    const authState: MicrosoftAuthState = { nonce, v: codeVerifier, o: window.location.origin };

    const params = new URLSearchParams({
        client_id: config.clientId,
        response_type: 'code',
        redirect_uri: `${config.url}/auth/redirect.html`,
        response_mode: 'query',
        scope: MICROSOFT_GRAPH_SCOPE,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
        state: btoa(JSON.stringify(authState))
    });

    const popup = window.open(`${MICROSOFT_AUTHORIZE_ENDPOINT}?${params}`, 'MicrosoftAuthPopup', 'width=500,height=600,left=200,top=50');
    if (!popup) {
        throw new Error('Popup was blocked by the browser.');
    }

    return new Promise<Authentication>((resolve, reject) => {
        const timeout = setTimeout(() => {
            window.removeEventListener('message', onMessage);
            reject(new Error('Sign-in timed out.'));
        }, MICROSOFT_POPUP_TIMEOUT_MS);

        const onMessage = (event: MessageEvent<MicrosoftPopupResult>) => {
            if (event.source !== popup || event.origin !== config.url || event.data?.nonce !== nonce) {
                return;
            }
            clearTimeout(timeout);
            window.removeEventListener('message', onMessage);
            popup.close();

            if (event.data.error || !event.data.accessToken || !event.data.userInfo) {
                reject(new Error(event.data.error_description || event.data.error || 'Microsoft sign-in failed.'));
                return;
            }

            resolve({
                provider: ExternalProvider.MICROSOFT,
                expireOn: new Date(Date.now() + (event.data.expiresIn ?? 0) * 1000),
                accessToken: event.data.accessToken,
                userInfo: event.data.userInfo
            });
        };

        window.addEventListener('message', onMessage);
    });
};

export const microsoftAdapter: ExternalProviderAdapter = {
    initialize: async () => {},
    authenticate: () => authenticateWithPopup(),
    available () {
        return Promise.resolve(getClientConfig().url !== undefined);
    }
};
